/**
 * Job statuses — docs/spec/02 §3, 04 §2.
 *
 * Two lists, and the difference between them matters.
 *
 * `JOB_STATUSES` is all **14** values the api can return. `FILTERABLE_STATUSES`
 * is the 5 that operations actually filter on. The queue must *render* any of
 * the 14 — a job can move into a downstream status (printed, shipped) while
 * still matching a list the client asked for — but it only *offers* the 5.
 *
 * Rendering an unknown status as a blank cell is the failure this guards
 * against: a row that looks empty rather than one that says `shipped`.
 */

/** Every status the api can return (verified against `api:openapi.yaml`). */
export const JOB_STATUSES = [
  'open',
  'transcription_in_progress',
  'transcription_completed',
  'transcription_qa_check',
  'transcription_auto_approved',
  'transcription_approved',
  'screening_pending',
  'screening_approved',
  'screening_rejected',
  'fulfillment_processing',
  'printed',
  'shipped',
  'delivered',
  'invalid_rejected',
] as const

export type JobStatus = (typeof JOB_STATUSES)[number]

const STATUS_SET: ReadonlySet<string> = new Set(JOB_STATUSES)

/** Narrow an arbitrary string to a known status. */
export function isJobStatus(value: unknown): value is JobStatus {
  return typeof value === 'string' && STATUS_SET.has(value)
}

/**
 * The only two statuses a transcriber may write an envelope into.
 *
 * **Expressed as the allowed set, never as a list of disallowed ones** (06 R3).
 * A denylist has to be extended every time the api grows a status, and the one
 * it forgets is the one that lets a duplicate submit through. This way an
 * unrecognised status is refused by default, which is the safe direction.
 */
export const TRANSCRIBER_EDITABLE_STATUSES = ['open', 'transcription_in_progress'] as const

const EDITABLE_SET: ReadonlySet<string> = new Set(TRANSCRIBER_EDITABLE_STATUSES)

/**
 * Has this job moved beyond the point where a transcriber may submit it?
 *
 * The 06 R3 double-submit guard. Note `transcription_completed` counts as past:
 * it is the status a job actually sits in for the ~2 seconds between the submit
 * landing and the api routing it onward (observed on staging 2026-07-29), so it
 * is precisely the window a second click would land in — and the earlier
 * spelled-out denylist in 06 R3 omitted it.
 */
export function isPastTranscriberStage(status: string): boolean {
  return !EDITABLE_SET.has(status)
}

/**
 * The statuses the queue filter offers (04 §2).
 *
 * This is the legacy client's list, verbatim and in its order — see
 * `job-list-filter.component.ts` `statusOptions` in the Angular app. Operations
 * staff use that app today, so an option that silently disappeared in the
 * rebuild would read as lost functionality, and one that appeared in a
 * different order would slow down people who filter by muscle memory.
 *
 * It is a subset of the 14: `transcription_completed`,
 * `transcription_approved`, the `screening_*` trio and `delivered` are real
 * statuses the queue can RENDER but has never offered as filters.
 */
export const FILTERABLE_STATUSES = [
  'open',
  'transcription_in_progress',
  'transcription_qa_check',
  'transcription_auto_approved',
  'invalid_rejected',
  'fulfillment_processing',
  'printed',
  'shipped',
] as const satisfies readonly JobStatus[]

export type FilterableStatus = (typeof FILTERABLE_STATUSES)[number]

const FILTERABLE_SET: ReadonlySet<string> = new Set(FILTERABLE_STATUSES)

export function isFilterableStatus(value: unknown): value is FilterableStatus {
  return typeof value === 'string' && FILTERABLE_SET.has(value)
}

/**
 * Human labels. Every one of the 14 has one, so no status renders as raw
 * snake_case.
 *
 * The eight filterable labels match the legacy client's wording exactly. These
 * are the words operations staff already say to each other; renaming
 * "Transcription QA Check" to a tidier "QA Check" would be a gratuitous
 * retraining cost for no gain.
 */
const STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
  open: 'Open',
  transcription_in_progress: 'Transcription In Progress',
  transcription_completed: 'Transcription Completed',
  transcription_qa_check: 'Transcription QA Check',
  transcription_auto_approved: 'Transcription Auto Approved',
  transcription_approved: 'Transcription Approved',
  screening_pending: 'Screening Pending',
  screening_approved: 'Screening Approved',
  screening_rejected: 'Screening Rejected',
  fulfillment_processing: 'Fulfillment Processing',
  printed: 'Printed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  invalid_rejected: 'Invalid Rejected',
}

/**
 * A display label for any status — including one this build has never heard
 * of. If the api adds a 15th status, `some_new_status` renders as
 * "Some New Status" rather than as an empty cell or a crash.
 */
export function statusLabel(status: string): string {
  if (isJobStatus(status)) return STATUS_LABELS[status]
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Mantine palette key per status.
 *
 * Colour is an *accompaniment* here, never the signal — the badge always
 * carries its text label too (07 §3: status must not be conveyed by colour
 * alone).
 */
const STATUS_COLORS: Readonly<Record<JobStatus, string>> = {
  open: 'blue',
  transcription_in_progress: 'yellow',
  transcription_completed: 'teal',
  transcription_qa_check: 'grape',
  transcription_auto_approved: 'green',
  transcription_approved: 'green',
  screening_pending: 'orange',
  screening_approved: 'green',
  screening_rejected: 'red',
  fulfillment_processing: 'cyan',
  printed: 'indigo',
  shipped: 'indigo',
  delivered: 'gray',
  invalid_rejected: 'red',
}

export function statusColor(status: string): string {
  return isJobStatus(status) ? STATUS_COLORS[status] : 'gray'
}
